import axios, { AxiosHeaders } from "axios";

import { apiUrl } from "@/shared/api/config";
import { resolveTimeout } from "@/shared/lib/http-timeout";
import { notifyApiOutage } from "@/shared/lib/maintenance-store";
import { checkIsTauri } from "@/shared/lib/tauri";

export const api = axios.create({
  baseURL: apiUrl,
  withCredentials: true,
  // KAN-595: таймаут проставляет интерсептор ниже, по методу запроса. Держать
  // его здесь нельзя: axios мержит defaults в конфиг ДО интерсепторов, поэтому
  // «вызывающий не задал таймаут» отличить от заданного уже невозможно.
  timeout: 0,
  headers: {
    "X-Client-Info":
      process.env.NODE_ENV !== "production"
        ? "GoodLuck-Frontend-Debug"
        : "GoodLuck-Frontend",
  },
});

let csrfToken: string | null = null;
let csrfFetchPromise: Promise<string | null> | null = null;

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const parts = document.cookie.split("; ").map((x) => x.split("="));
  for (const [k, v] of parts) {
    if (k === name) return v ? decodeURIComponent(v) : "";
  }
  return null;
}

function setCookie(name: string, value: string) {
  if (typeof document === "undefined") return;
  const secure =
    typeof window !== "undefined" && window.location?.protocol === "https:";
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Path=/; SameSite=Lax${secure ? "; Secure" : ""}`;
}

function isUnsafeMethod(method?: string) {
  const m = (method || "get").toLowerCase();
  return m === "post" || m === "put" || m === "patch" || m === "delete";
}

async function ensureCsrfToken(): Promise<string | null> {
  const fromCookie = getCookie("csrf");
  if (fromCookie) {
    csrfToken = fromCookie;
    csrfFetchPromise = null;
    return csrfToken;
  }
  if (csrfToken) return csrfToken;

  // Deduplicate concurrent requests: reuse in-flight promise
  if (csrfFetchPromise) return csrfFetchPromise;

  csrfFetchPromise = (async () => {
    try {
      const url = `${apiUrl}/auth/csrf`;
      const r = await axios.get(url, { withCredentials: true, timeout: 8000 });
      const token = r.data?.token || getCookie("csrf");
      csrfToken = typeof token === "string" && token.length > 0 ? token : null;
      if (csrfToken && !getCookie("csrf")) setCookie("csrf", csrfToken);
      return csrfToken;
    } catch {
      return null;
    } finally {
      csrfFetchPromise = null;
    }
  })();

  return csrfFetchPromise;
}

// Dev mock mode: подключаем мок-адаптер динамически ТОЛЬКО в браузере
const useDevAuth =
  typeof window !== "undefined" && process.env["NEXT_PUBLIC_DEV_AUTH"] === "1";
if (useDevAuth) {
  import("@/shared/lib/mock-api")
    .then(({ createMockAxiosAdapter }) => {
      api.defaults.adapter = createMockAxiosAdapter();
    })
    .catch(() => {
      // игнорируем ошибки загрузки моков в деве
    });
}

// KAN-316: замер длительности запросов — старт в request-интерсепторе
api.interceptors.request.use((config) => {
  if (checkIsTauri()) config.headers.set("X-Client-Info", "GoodLuck-Tauri");
  (config as { metadata?: { startTime: number } }).metadata = {
    startTime: typeof performance !== "undefined" ? performance.now() : Date.now(),
  };
  // KAN-595: 20 с на чтение, 30 с на мутацию, явный таймаут вызывающего в
  // приоритете. Раньше всё висело минуту — см. resolveTimeout.
  config.timeout = resolveTimeout(config.method, config.timeout);
  return config;
});

api.interceptors.request.use(async (config) => {
  if (useDevAuth) return config;
  if (!isUnsafeMethod(config.method)) return config;
  if (config.url?.includes("/auth/csrf")) return config;

  // Здесь раньше стояло «if (timeout === undefined) timeout = 15000» — код,
  // который не исполнялся ни разу: defaults уже подставили 60000 (KAN-595).
  // Таймаут теперь назначается выше, в интерсепторе метрик.

  const token = await ensureCsrfToken();
  if (!token) return config;

  config.headers = config.headers ?? new AxiosHeaders();
  config.headers.set("X-CSRF", token);
  return config;
});

// KAN-316: медленные запросы (>1с) уходят в RUM с шаблоном маршрута.
// Отменённые/сетевые не считаем «медленными»; beacon-эндпоинт не меряем.
const SLOW_API_THRESHOLD_MS = 1000;

function reportApiDuration(config: unknown, status: number | undefined): void {
  if (typeof window === "undefined") return;
  const cfg = config as {
    metadata?: { startTime: number };
    url?: string;
    method?: string;
  } | null;
  const start = cfg?.metadata?.startTime;
  const url = cfg?.url ?? "";
  if (start === undefined || !url || url.includes("rum-metrics")) return;
  const duration =
    (typeof performance !== "undefined" ? performance.now() : Date.now()) - start;
  if (duration < SLOW_API_THRESHOLD_MS || status === undefined) return;
  void import("@/shared/lib/rum/rum-sender").then(({ rumEnqueue, normalizeRoute }) => {
    rumEnqueue(
      "ApiDuration",
      duration,
      `${(cfg?.method ?? "get").toUpperCase()} ${normalizeRoute(url, "api")}`,
    );
  });
}

api.interceptors.response.use(
  (r) => {
    reportApiDuration(r.config, r.status);
    return r;
  },
  async (err) => {
    const status = (err as { response?: { status?: number } })?.response?.status;
    reportApiDuration((err as { config?: unknown })?.config, status);
    if (useDevAuth) {
      throw err;
    }
    // KAN-283: 502/503/504 или сетевой сбой по /api → сигналим оверлею «сайт
    // обновляется» (он сам подтвердит недоступность через /health и авто-скроется).
    // Обычные 4xx (бизнес-ошибки) и отменённые запросы не трогаем.
    if (!axios.isCancel(err)) {
      const isOutage =
        status === 502 ||
        status === 503 ||
        status === 504 ||
        (!err?.response && err?.code !== "ERR_CANCELED");
      if (isOutage) notifyApiOutage();
    }
    throw err;
  },
);
